import { InternalRoleSchema } from '@buyerflo/core/schemas/roles'
import { ChevronRight, Plus } from '@tamagui/lucide-icons-2'
import { useRolesApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Button from 'components/button'
import Loading from 'components/Loading'
import { sortBy } from 'ramda'
import { useState } from 'react'
import { SizableText, Text, View, XStack, YStack } from 'tamagui'
import { RoleDrawer } from './roleDrawer'

const HeaderCell = ({ children, flex, width }: { children?: React.ReactNode; flex?: number; width?: number }) => (
    <View
        flex={flex}
        width={width}
        paddingHorizontal="$2"
    >
        <Text
            fontWeight="600"
            fontSize="$2"
            color="$colorMuted"
            textTransform="uppercase"
            letterSpacing={0.5}
        >
            {children}
        </Text>
    </View>
)

export default function RolesPanel() {
    const rolesApi = useRolesApi()
    const usersApi = useUsersApi()
    const { user } = useUserData()
    const [editRole, setEditRole] = useState<{} | InternalRoleSchema | undefined>()

    const canEdit = user.permissions.includes('roles.edit')

    if (!rolesApi.data) {
        return <Loading />
    }

    const roles = sortBy(role => role.roleName.toLowerCase(), rolesApi.data)
    const userCount = (roleId: string) => (usersApi.data ?? []).filter(u => u.roleId === roleId).length

    return (
        <YStack gap="$3">
            <XStack
                alignItems="center"
                justifyContent="space-between"
                gap="$3"
            >
                <SizableText
                    size="$2"
                    color="$colorMuted"
                >
                    {roles.length} {roles.length === 1 ? 'role' : 'roles'} · click any to view or edit its permissions
                </SizableText>
                {canEdit && (
                    <Button
                        theme="primary"
                        fontWeight="600"
                        borderRadius="$4"
                        icon={<Plus size={16} />}
                        onPress={() => setEditRole({})}
                        data-help-id="roles-new-button"
                    >
                        New Role
                    </Button>
                )}
            </XStack>

            <YStack
                backgroundColor="$backgroundStrong"
                borderWidth={1}
                borderColor="$borderColor"
                borderRadius={14}
                overflow="hidden"
                data-help-id="roles-list"
            >
                <XStack
                    backgroundColor="$background"
                    borderBottomWidth={1}
                    borderBottomColor="$borderColor"
                    paddingVertical="$3"
                    paddingHorizontal="$4"
                >
                    <HeaderCell flex={1.6}>Role</HeaderCell>
                    <HeaderCell flex={1}>Permissions</HeaderCell>
                    <HeaderCell flex={0.6}>Users</HeaderCell>
                    <View width={28} />
                </XStack>

                {roles.map((role, index) => (
                    <XStack
                        key={role.id}
                        paddingVertical="$3"
                        paddingHorizontal="$4"
                        alignItems="center"
                        cursor="pointer"
                        animation="quick"
                        hoverStyle={{ backgroundColor: '$backgroundHover' }}
                        pressStyle={{ backgroundColor: '$backgroundPress' }}
                        borderBottomWidth={index < roles.length - 1 ? 1 : 0}
                        borderBottomColor="$borderColor"
                        onPress={() => setEditRole(role)}
                    >
                        <View
                            flex={1.6}
                            paddingHorizontal="$2"
                        >
                            <Text
                                fontSize="$3"
                                fontWeight="600"
                                numberOfLines={1}
                            >
                                {role.roleName}
                            </Text>
                        </View>
                        <View
                            flex={1}
                            paddingHorizontal="$2"
                        >
                            <Text
                                fontSize="$2"
                                color="$colorSoft"
                            >
                                {role.rolePermissions.length} enabled
                            </Text>
                        </View>
                        <View
                            flex={0.6}
                            paddingHorizontal="$2"
                        >
                            <Text
                                fontSize="$2"
                                fontFamily="$mono"
                                color="$colorSoft"
                            >
                                {userCount(role.id) || '—'}
                            </Text>
                        </View>
                        <View
                            width={28}
                            alignItems="center"
                        >
                            <ChevronRight
                                size={16}
                                color="$colorFaint"
                            />
                        </View>
                    </XStack>
                ))}
            </YStack>

            {editRole !== undefined && (
                <RoleDrawer
                    key={(editRole as InternalRoleSchema).id ?? 'new'}
                    role={editRole}
                    users={usersApi.data ?? []}
                    onClose={() => setEditRole(undefined)}
                />
            )}
        </YStack>
    )
}
